import { NavLink, useLocation } from 'react-router-dom';
import { LayoutGrid, Package, ShoppingCart, FileText, Settings, AlertTriangle, User, CloudOff, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOutbox } from '../lib/useOutbox';
import OutboxPanel from './OutboxPanel';

const navItems = [
  { path: '/', icon: LayoutGrid, label: 'Home', labelNp: 'होम' },
  { path: '/inventory', icon: Package, label: 'Items', labelNp: 'सामान' },
  { path: '/sale', icon: ShoppingCart, label: 'Sell', labelNp: 'बेच्नु' },
  { path: '/reports', icon: FileText, label: 'Reports', labelNp: 'रिपोर्ट' },
  { path: '/settings', icon: Settings, label: 'Settings', labelNp: 'सेटिङ' },
];

export default function Layout({ children, lowStockCount = 0 }) {
  const location = useLocation();
  const { shopName, shopNameEn, userName, userRole } = useAuth();
  // Mounted here because Layout wraps every authenticated page, so the queue
  // drains no matter where the shopkeeper is when signal returns.
  const { online, connected, pending, failed, syncing, syncNow, refresh } = useOutbox();
  const [showOutbox, setShowOutbox] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F9F5] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[#8B0000] font-nepali">{shopName || 'पसल साथी'}</h1>
            <p className="text-xs text-gray-500">{shopNameEn || 'Pasal Sathi'}</p>
          </div>
          
          <div className="flex items-center gap-2">
            {userName && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full">
                <User className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">{userName}</span>
                <span className="text-xs text-gray-500 capitalize">({userRole})</span>
              </div>
            )}
            
            {lowStockCount > 0 && (
              <NavLink 
                to="/inventory?filter=low-stock" 
                className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-sm"
                data-testid="low-stock-alert-btn"
              >
                <AlertTriangle className="w-4 h-4" />
                <span>{lowStockCount}</span>
              </NavLink>
            )}
          </div>
        </div>

        {/* Connectivity / unsynced work. Shown only when there is something to
            say, so the normal case stays uncluttered. */}
        {(!connected || pending > 0 || failed > 0) && (
          <div
            className={`w-full flex items-stretch border-t ${
              failed > 0
                ? 'bg-red-50 border-red-200'
                : connected
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-gray-100 border-gray-200'
            }`}
            data-testid="offline-status-bar"
          >
            <button
              onClick={syncNow}
              disabled={!online || syncing}
              className={`flex-1 px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium ${
                failed > 0
                  ? 'text-red-800'
                  : connected
                    ? 'text-amber-800'
                    : 'text-gray-600'
              }`}
            >
              {failed > 0 ? (
                <AlertTriangle className="w-4 h-4" />
              ) : connected ? (
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              ) : (
                <CloudOff className="w-4 h-4" />
              )}
              <span className="font-nepali">
                {failed > 0
                  ? `${failed} अस्वीकृत`
                  : pending > 0
                    ? `${pending} ${pending === 1 ? 'change' : 'changes'} पठाउन बाँकी`
                    : 'काम सुरक्षित हुनेछ'}
                {/* Name the actual reason. "No internet" when the server is
                    merely asleep sends the shopkeeper to check their phone. */}
                {failed === 0 && !online && ' — इन्टरनेट छैन'}
                {failed === 0 && online && !connected && ' — server not reachable'}
                {failed === 0 && connected && pending > 0 && !syncing && ' — tap to sync'}
              </span>
            </button>
            {/* Rejected writes need somewhere to be seen and acted on, not just
                a count. */}
            {(failed > 0 || pending > 0) && (
              <button
                onClick={() => setShowOutbox(true)}
                className="px-4 text-sm underline decoration-dotted"
                data-testid="view-outbox-btn"
              >
                View
              </button>
            )}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="px-4 py-4">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 bottom-nav z-40" data-testid="bottom-nav">
        <div className="flex items-center justify-around py-2 max-w-lg mx-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center py-2 px-4 rounded-xl transition-colors ${
                  isActive 
                    ? 'text-[#8B0000]' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className={`w-6 h-6 ${isActive ? 'stroke-[2.5px]' : ''}`} />
                <span className="text-xs mt-1 font-medium">{item.labelNp}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <OutboxPanel
        isOpen={showOutbox}
        onClose={() => setShowOutbox(false)}
        onChanged={refresh}
      />
    </div>
  );
}
